/**
 * Which lines Tibbs says (docs/TIBBS-VOICE.md 3.2-3.5 and 4.1). Pure and
 * deterministic: the same facts, memory and seed give the same lines on every
 * device, so an online table hears one readout. No DOM, no audio.
 *
 * Readouts are chosen silently at every hole end (so the game memory evolves
 * the same way whether or not anyone asks), and only played when someone asks.
 */
import { mixSeed, nextFloat } from '../../engine/rng';
import type { GameState } from '../../engine';
import type { HoleFacts } from './facts';
import { holeFacts } from './facts';
import { LINE_BY_ID, PRAISE, linesFor } from './library';
import type { Line, Trigger } from './library';

export interface LinePick {
  lineId: string;
  /** Who fills {name} (seats[0]) and {name2} (seats[1]). */
  seats: number[];
}

export interface Readout {
  key: string;
  hole: number;
  lead: LinePick | null;
  tail: LinePick | null;
}

export type BeatKind = 'open' | 'highlight' | 'scenic' | 'bronze' | 'silver' | 'gold' | 'twoPlayer' | 'close';

export interface CeremonyBeat {
  kind: BeatKind;
  lineId: string;
  /**
   * Names in the line. For a line with one {name} slot, several seats mean a
   * shared medal ("A. And B."); for a twoNames line, seats[1] fills {name2}.
   */
  seats: number[];
  /** Highlight only: the chip under the medallion ("Best hole −24 · Olympia"). */
  chip?: string;
}

export interface Ceremony {
  beats: CeremonyBeat[];
  /** Places for the podium: gold, silver and bronze seats, the Scenic Route seats. */
  gold: number[];
  silver: number[];
  bronze: number[];
  scenic: number[];
}

interface HoleMark {
  hole: number;
  trigger: Trigger | null;
  seat: number | null;
  dig: boolean;
}

export interface GameMemory {
  v: 1;
  /** Line ids chosen this game (never chosen twice). */
  used: string[];
  leads: HoleMark[];
  tails: HoleMark[];
  tics: Record<string, number>;
  ohDear: boolean;
  asides: number;
  runaway: boolean;
  /** Seats that have led alone after some hole. */
  everLed: number[];
  eurekas: number[];
  outs: number[];
  skips: number[];
  bestHole: { seat: number; score: number; hole: number; world: number } | null;
  worstHole: { seat: number; score: number; hole: number; world: number } | null;
  /** Sole/shared leaders and last places after each regulation hole. */
  leadHistory: number[][];
  lastHistory: number[][];
  /** Holes already recorded, by key ('h3'). */
  readouts: Record<string, Readout>;
  ceremony: Ceremony | null;
}

export interface SelectCtx {
  /** Per-game seed: the same on every device at the table. */
  seed: number;
  /** Vs computer: the member's privilege and the device history apply. */
  solo: boolean;
  /** The device's own seat (the club's member), if any. */
  me: number | null;
  /** Solo only: recently played line ids on this device, oldest first. */
  history?: readonly string[];
  /** Can this line be played right now (its clips cached, or online)? Default: yes. */
  available?: (lineId: string) => boolean;
  /** Clip length in seconds, from the manifest. Default: estimated from the text. */
  durOf?: (lineId: string) => number | undefined;
}

export function emptyMemory(): GameMemory {
  return {
    v: 1,
    used: [],
    leads: [],
    tails: [],
    tics: {},
    ohDear: false,
    asides: 0,
    runaway: false,
    everLed: [],
    eurekas: [],
    outs: [],
    skips: [],
    bestHole: null,
    worstHole: null,
    leadHistory: [],
    lastHistory: [],
    readouts: {},
    ceremony: null,
  };
}

export const holeKey = (hole: number) => `h${hole}`;

// ---------------------------------------------------------------------------
// Budgets, tics, randomness
// ---------------------------------------------------------------------------

export const LEAD_MAX_S = 10;
export const TAIL_MAX_S = 7;
export const READOUT_MAX_S = 16;
/** A lead longer than this leaves room only for the tails that always win. */
const LONG_LEAD_S = 9;

const TICS: [string, RegExp, number][] = [
  ['ohDear', /\bOh dear\b/, 1],
  ['remarkable', /\bRemarkable\b/, 2],
  ['experience', /\bIn my experience\b/i, 2],
];

/** Rough spoken length: about 130 words a minute plus the tagged pauses. */
export function estimateDur(l: Line): number {
  const words = l.text.replace(/\[[^\]]*\]/g, ' ').split(/\s+/).filter(Boolean).length;
  const pauses = (l.text.match(/\[beat\]/g)?.length ?? 0) * 0.45 + (l.text.match(/\[pause\]/g)?.length ?? 0) * 0.9 + (l.text.match(/\[long pause\]/g)?.length ?? 0) * 1.4;
  return (words / 130) * 60 + pauses;
}

function durFor(ctx: SelectCtx, l: Line): number {
  return ctx.durOf?.(l.id) ?? estimateDur(l);
}

function rand(seed: number, hole: number, slot: number): number {
  return nextFloat(mixSeed(seed, hole, slot))[0];
}

/**
 * Weighted pick. With a device history (solo), candidates are sorted
 * least-recently played first and the pick is among the least-recent third.
 */
export function pickLine(cands: readonly Line[], r: number, history?: readonly string[]): Line | null {
  if (!cands.length) return null;
  let pool = cands.slice();
  if (history && history.length) {
    const last = new Map<string, number>();
    history.forEach((id, i) => last.set(id, i));
    pool.sort((a, b) => (last.get(a.id) ?? -1) - (last.get(b.id) ?? -1) || cands.indexOf(a) - cands.indexOf(b));
    pool = pool.slice(0, Math.max(1, Math.ceil(pool.length / 3)));
  }
  const total = pool.reduce((s, l) => s + l.weight, 0);
  let x = r * total;
  for (const l of pool) {
    x -= l.weight;
    if (x < 0) return l;
  }
  return pool[pool.length - 1]!;
}

// ---------------------------------------------------------------------------
// Tag eligibility
// ---------------------------------------------------------------------------

interface Env {
  world?: number;
  card?: number;
  many?: boolean;
  double5?: boolean;
  twoNames?: boolean;
  solo?: boolean;
  full?: boolean;
  left?: number;
  nextWorld?: number | null;
  playoffNext?: 'start' | 'again' | null;
  firstLead?: boolean;
  recap?: string;
  /** Ceremony variants that hold (wire, close, playoff, far, clear). */
  variants?: ReadonlySet<string>;
}

const IGNORED = new Set(['dig', 'you', 'aside']);
const VARIANT_TAGS = new Set(['wire', 'close', 'playoff', 'far', 'clear']);
const RECAP_TAGS = new Set(['best', 'eurekas', 'wire', 'comeback', 'bravest', 'patient', 'outs']);

function tagOk(tag: string, env: Env, trigger: Trigger): boolean {
  if (IGNORED.has(tag) || tag.startsWith('tic:')) return true;
  const [k, v] = tag.split(':');
  if (v !== undefined) {
    const n = Number(v);
    if (k === 'world') return env.world === n;
    if (k === 'card') return env.card === n;
    if (k === 'left') return env.left === n;
    if (k === 'next') return env.nextWorld === n;
    return false;
  }
  if (trigger === 'recap' && RECAP_TAGS.has(tag)) return env.recap === tag;
  if (VARIANT_TAGS.has(tag)) return !!env.variants?.has(tag);
  switch (tag) {
    case 'many':
      return !!env.many;
    case 'double5':
      return !!env.double5;
    case 'twoNames':
      return !!env.twoNames;
    case 'solo':
      return !!env.solo;
    case 'full':
      return !!env.full;
    case 'short':
      return env.full === false;
    case 'start':
    case 'again':
      return env.playoffNext === tag;
    case 'firstLead':
      return !!env.firstLead;
    default:
      return false;
  }
}

const ticCount = (re: RegExp, text: string) => (text.match(new RegExp(re.source, re.flags.includes('g') ? re.flags : re.flags + 'g')) ?? []).length;

function ticsOk(l: Line, mem: GameMemory): boolean {
  for (const [name, re, cap] of TICS) {
    const k = ticCount(re, l.text);
    if (k && (mem.tics[name] ?? 0) + k > cap) return false;
  }
  return true;
}

/** Lines of a trigger that can be said now: unused, tags satisfied, tics under cap, clips available. */
function eligible(trigger: Trigger, env: Env, mem: GameMemory, ctx: SelectCtx, extra?: (l: Line) => boolean): Line[] {
  const used = new Set(mem.used);
  let out = linesFor(trigger).filter(
    (l) => !used.has(l.id) && l.tags.every((t) => tagOk(t, env, trigger)) && ticsOk(l, mem) && (ctx.available?.(l.id) ?? true) && (!extra || extra(l)),
  );
  // A variant line (wire to wire, a close finish) beats the generic ones when its fact holds.
  if (env.variants && out.some((l) => l.tags.some((t) => VARIANT_TAGS.has(t)))) out = out.filter((l) => l.tags.some((t) => VARIANT_TAGS.has(t)));
  return out;
}

function within(lines: Line[], ctx: SelectCtx, max: number): Line[] {
  const fit = lines.filter((l) => durFor(ctx, l) <= max);
  return fit.length ? fit : lines;
}

function commit(mem: GameMemory, l: Line): void {
  mem.used.push(l.id);
  for (const [name, re] of TICS) {
    const k = ticCount(re, l.text);
    if (k) mem.tics[name] = (mem.tics[name] ?? 0) + k;
  }
  if (l.trigger === 'ohDear') mem.ohDear = true;
  if (l.tags.includes('aside')) mem.asides++;
  if (l.trigger === 'runawayLast') mem.runaway = true;
}

// ---------------------------------------------------------------------------
// The lead
// ---------------------------------------------------------------------------

interface Cand {
  trigger: Trigger;
  seat: number | null;
  /** leadChange: the old leader, for the twoNames line. */
  seat2?: number;
  env: Env;
}

const DIG_TRIGGERS = new Set<Trigger>(['rushed', 'hm', 'ohDear', 'caught']);

/** Lead candidates in priority order (3.2), before the balance rules. Exported for tests. */
export function leadCandidates(f: HoleFacts, mem: GameMemory): Cand[] {
  const out: Cand[] = [];
  const asc = (a: { score: number }, b: { score: number }) => a.score - b.score;
  const desc = (a: { score: number }, b: { score: number }) => b.score - a.score;
  const ps = f.players;
  const push = (trigger: Trigger, list: typeof ps, env: (p: (typeof ps)[number]) => Env = () => ({})) => {
    for (const p of list) out.push({ trigger, seat: p.seat, env: env(p) });
  };
  push('legendary40', ps.filter((p) => p.score <= -40));
  // Four matched columns always score exactly −20, so the bonus outranks the generic legendary line or its lines would never be heard.
  push('bonus4', ps.filter((p) => p.bonusK >= 4).sort(asc));
  push('legendary', ps.filter((p) => p.score <= -20).sort(asc), (p) => ({ double5: p.eurekaPairs >= 2 }));
  push('bonus3', ps.filter((p) => p.bonusK === 3).sort(asc));
  if (!mem.ohDear) push('ohDear', ps.filter((p) => p.score >= 30).sort(desc));
  push('eurekaPair', ps.filter((p) => p.eurekaPairs >= 1).sort(asc));
  push('bonus2', ps.filter((p) => p.bonusK === 2).sort(asc));
  push('rushed', ps.filter((p) => p.wentOut && p.score >= f.low + 8).sort(desc));
  const caught = ps.filter((p) => p.caughtValues.length > 0).sort((a, b) => sumOf(b.caughtValues) - sumOf(a.caughtValues));
  push('caught', caught, (p) => ({ card: Math.max(...p.caughtValues), many: p.caughtValues.length >= 2 }));
  push('bigPair', ps.filter((p) => p.bigPair !== null).sort((a, b) => (b.bigPair ?? 0) - (a.bigPair ?? 0)), (p) => ({ card: p.bigPair ?? 0 }));
  for (const p of ps.filter((x) => x.skips >= 3).sort((a, b) => b.skips - a.skips)) {
    out.push({ trigger: f.lowSeats.includes(p.seat) ? 'patienceWon' : 'patienceLost', seat: p.seat, env: {} });
  }
  push('hm', ps.filter((p) => p.score >= 20 && (p.score < 30 || mem.ohDear)).sort(desc));
  push('outAndWon', ps.filter((p) => p.wentOut && p.score === f.low));
  const soleLow = f.lowSeats.length === 1 ? ps.filter((p) => p.seat === f.lowSeats[0]) : [];
  if (f.low === 0) push('zero', soleLow);
  if (f.low < 0) push('lowUnder', soleLow);
  if (f.lowSeats.length >= 2) out.push({ trigger: 'halved', seat: null, env: {} });
  if (f.reshuffles > 0) out.push({ trigger: 'reshuffle', seat: null, env: {} });
  return out;
}

const sumOf = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

function pickLead(f: HoleFacts, mem: GameMemory, ctx: SelectCtx): { line: Line; seat: number | null } | null {
  let cands = leadCandidates(f, mem);
  const prev = mem.leads[mem.leads.length - 1];
  const prev2 = mem.leads[mem.leads.length - 2];
  // No player is the subject of a dig two holes running (quiet is always there instead).
  cands = cands.filter((c) => !(DIG_TRIGGERS.has(c.trigger) && prev?.dig && prev.seat === c.seat && prev.hole === f.hole - 1));
  // Two digs in a row: praise wins whenever it is eligible.
  if (prev?.dig && prev2?.dig) cands = [...cands.filter((c) => PRAISE.has(c.trigger)), ...cands.filter((c) => !PRAISE.has(c.trigger))];
  let slot = 10;
  for (const c of cands) {
    const lines = within(eligible(c.trigger, c.env, mem, ctx), ctx, LEAD_MAX_S);
    const line = pickLine(lines, rand(ctx.seed, f.hole, slot++), ctx.history);
    if (line) return { line, seat: c.seat };
  }
  const quiet = pickQuiet(f, mem, ctx);
  return quiet ? { line: quiet, seat: null } : null;
}

function pickQuiet(f: HoleFacts, mem: GameMemory, ctx: SelectCtx): Line | null {
  const r = (slot: number) => rand(ctx.seed, f.hole, slot);
  const isAside = (l: Line) => l.tags.includes('aside');
  const isPostcard = (l: Line) => l.tags.some((t) => t.startsWith('world:'));
  const env: Env = { world: f.world };
  const all = eligible('quiet', env, mem, ctx);
  const asides = all.filter(isAside);
  const postcards = all.filter(isPostcard);
  const plain = all.filter((l) => !isAside(l) && !isPostcard(l));
  const tries: Line[][] = [];
  if (r(1) < 0.25 && mem.asides < 1) tries.push(asides);
  if (f.lap === 1) tries.push(postcards);
  tries.push(plain, postcards, asides);
  for (const [i, list] of tries.entries()) {
    const l = pickLine(list, r(2 + i), ctx.history);
    if (l) return l;
  }
  return null;
}

// ---------------------------------------------------------------------------
// The tail
// ---------------------------------------------------------------------------

/** Tail triggers that always win their slot and ignore the length budget. */
const ALWAYS = new Set<Trigger>(['playoff', 'homeStretch', 'leadChange']);

function recentTail(mem: GameMemory, trigger: Trigger, hole: number, within: number): HoleMark | undefined {
  return [...mem.tails].reverse().find((t) => t.trigger === trigger && hole - t.hole < within);
}

/** Tail candidates in priority order. Exported for tests. */
export function tailCandidates(f: HoleFacts, mem: GameMemory, ctx: SelectCtx, lead: { line: Line; seat: number | null } | null): Cand[] {
  const out: Cand[] = [];
  if (f.playoffNext) return [{ trigger: 'playoff', seat: null, env: { playoffNext: f.playoffNext } }];
  if (f.playoff || f.gameOver) return out;
  if (f.holesLeft === 1 || f.holesLeft === 2) return [{ trigger: 'homeStretch', seat: null, env: { left: f.holesLeft, nextWorld: f.nextWorld } }];
  if (f.hole <= 1) return out;
  const n = f.playerCount;
  const leadDig = lead ? DIG_TRIGGERS.has(lead.line.trigger) : false;
  const sameSet = (a: number[], b: number[]) => a.length === b.length && a.every((x) => b.includes(x));

  const leader = f.leadersAfter.length === 1 ? f.leadersAfter[0]! : null;
  if (leader !== null && !(f.leadersBefore.length === 1 && f.leadersBefore[0] === leader)) {
    const old = f.leadersBefore.length === 1 ? f.leadersBefore[0]! : null;
    out.push({
      trigger: 'leadChange',
      seat: leader,
      env: { twoNames: old !== null, firstLead: !mem.everLed.includes(leader) },
      ...(old !== null ? { seat2: old } : {}),
    });
  }
  if (f.leadersAfter.length >= 2 && !sameSet(f.leadersAfter, f.leadersBefore)) {
    out.push({ trigger: 'tieLead', seat: f.leadersAfter[0]!, env: { twoNames: f.leadersAfter.length === 2 } });
  }
  if (leader !== null && f.hole >= 4 && f.gap12 > 0 && f.gap12 <= 3) out.push({ trigger: 'closeRace', seat: leader, env: {} });
  const half = Math.ceil(n / 2);
  const climbers = f.players
    .filter((p) => p.rankBefore - p.rankAfter >= 2 || (n >= 3 && p.rankBefore === Math.max(...f.players.map((x) => x.rankBefore)) && p.rankBefore > half && p.rankAfter <= half))
    .sort((a, b) => b.rankBefore - b.rankAfter - (a.rankBefore - a.rankAfter));
  if (climbers[0]) out.push({ trigger: 'comeback', seat: climbers[0].seat, env: {} });
  if (ctx.solo && ctx.me !== null && !recentTail(mem, 'member', f.hole, 3) && !recentTail(mem, 'memberLeads', f.hole, 3)) {
    const mine = f.players.find((p) => p.seat === ctx.me);
    if (mine && leader === ctx.me) out.push({ trigger: 'memberLeads', seat: ctx.me, env: { solo: true } });
    else if (mine && mine.rankAfter > half) out.push({ trigger: 'member', seat: ctx.me, env: { solo: true } });
  }
  if (leader !== null && f.gap12 >= 15 && !recentTail(mem, 'leaderAway', f.hole, 3)) out.push({ trigger: 'leaderAway', seat: leader, env: {} });
  const last = f.lastSeats.length === 1 ? f.lastSeats[0]! : null;
  const leadHitLast = leadDig && lead?.seat === last;
  if (last !== null && n >= 3 && !leadHitLast) {
    if (f.gapLast >= 40 && !mem.runaway) out.push({ trigger: 'runawayLast', seat: last, env: {} });
    const prevLast = [...mem.tails].reverse().find((t) => t.trigger === 'lastPlace');
    if (!recentTail(mem, 'lastPlace', f.hole, 3) && !(prevLast && prevLast.seat === last)) out.push({ trigger: 'lastPlace', seat: last, env: {} });
  }
  return out;
}

function pickTail(f: HoleFacts, mem: GameMemory, ctx: SelectCtx, lead: { line: Line; seat: number | null } | null): LinePick | null {
  const leadDur = lead ? durFor(ctx, lead.line) : 0;
  let slot = 40;
  for (const c of tailCandidates(f, mem, ctx, lead)) {
    const always = ALWAYS.has(c.trigger);
    if (!always && leadDur > LONG_LEAD_S) continue;
    const seat2 = c.seat2;
    const lines = eligible(c.trigger, c.env, mem, ctx, (l) => always || leadDur + 0.25 + durFor(ctx, l) <= READOUT_MAX_S);
    const line = pickLine(within(lines, ctx, TAIL_MAX_S), rand(ctx.seed, f.hole, slot++), ctx.history);
    if (!line) continue;
    commit(mem, line);
    const seats = c.seat === null ? [] : line.tags.includes('twoNames') && seat2 !== undefined ? [c.seat, seat2] : [c.seat];
    if (c.trigger === 'tieLead' && line.tags.includes('twoNames')) seats.splice(0, seats.length, ...f.leadersAfter.slice(0, 2));
    mem.tails.push({ hole: f.hole, trigger: c.trigger, seat: c.seat, dig: false });
    return { lineId: line.id, seats: /\{name\}/.test(line.text) ? seats : [] };
  }
  mem.tails.push({ hole: f.hole, trigger: null, seat: null, dig: false });
  return null;
}

// ---------------------------------------------------------------------------
// Recording a hole
// ---------------------------------------------------------------------------

function grow(xs: number[], n: number): number[] {
  const out = xs.slice();
  while (out.length < n) out.push(0);
  return out;
}

/** Fold one hole's facts into the game memory (stats for the ceremony). */
function record(mem: GameMemory, f: HoleFacts): void {
  mem.eurekas = grow(mem.eurekas, f.playerCount);
  mem.outs = grow(mem.outs, f.playerCount);
  mem.skips = grow(mem.skips, f.playerCount);
  for (const p of f.players) {
    mem.eurekas[p.seat]! += p.eurekaPairs;
    mem.skips[p.seat]! += p.skips;
    if (p.wentOut) mem.outs[p.seat]! += 1;
    if (!f.playoff) {
      if (!mem.bestHole || p.score < mem.bestHole.score) mem.bestHole = { seat: p.seat, score: p.score, hole: f.hole, world: f.world };
      if (!mem.worstHole || p.score > mem.worstHole.score) mem.worstHole = { seat: p.seat, score: p.score, hole: f.hole, world: f.world };
    }
  }
  if (!f.playoff) {
    mem.leadHistory.push(f.leadersAfter.slice());
    mem.lastHistory.push(f.lastSeats.slice());
    if (f.leadersAfter.length === 1 && !mem.everLed.includes(f.leadersAfter[0]!)) mem.everLed.push(f.leadersAfter[0]!);
  }
}

/**
 * Choose the readout for the hole that just ended and fold the hole into the
 * memory. Returns a new memory; the old one is untouched. A hole already
 * recorded returns its stored readout. The final hole gets no readout (the
 * ceremony takes its place) but is still recorded.
 */
export function selectReadout(f: HoleFacts, memory: GameMemory, ctx: SelectCtx): { readout: Readout; memory: GameMemory } {
  const key = holeKey(f.hole);
  const done = memory.readouts[key];
  if (done) return { readout: done, memory };
  const mem: GameMemory = structuredCloneMemory(memory);
  let readout: Readout = { key, hole: f.hole, lead: null, tail: null };
  if (!f.gameOver) {
    const lead = pickLead(f, mem, ctx);
    if (lead) {
      commit(mem, lead.line);
      mem.leads.push({ hole: f.hole, trigger: lead.line.trigger, seat: lead.seat, dig: DIG_TRIGGERS.has(lead.line.trigger) });
    } else mem.leads.push({ hole: f.hole, trigger: null, seat: null, dig: false });
    const tail = pickTail(f, mem, ctx, lead);
    readout = {
      key,
      hole: f.hole,
      lead: lead ? { lineId: lead.line.id, seats: lead.seat !== null && /\{name\}/.test(lead.line.text) ? [lead.seat] : [] } : null,
      tail,
    };
  }
  record(mem, f);
  mem.readouts[key] = readout;
  return { readout, memory: mem };
}

function structuredCloneMemory(m: GameMemory): GameMemory {
  return JSON.parse(JSON.stringify(m)) as GameMemory;
}

/** Convenience: facts + selection straight from a hole-over state. */
export function readoutFor(state: GameState, memory: GameMemory, ctx: SelectCtx): { readout: Readout; memory: GameMemory } {
  return selectReadout(holeFacts(state), memory, ctx);
}

/** Is this a normalized, current-version memory? */
export function normalizeMemory(raw: unknown): GameMemory {
  if (!raw || typeof raw !== 'object' || (raw as { v?: unknown }).v !== 1) return emptyMemory();
  return { ...emptyMemory(), ...(raw as GameMemory) };
}

// ---------------------------------------------------------------------------
// The ceremony (4.1)
// ---------------------------------------------------------------------------

const WORLD_NAMES = ['Giza Links', 'The Hanging Gardens', 'Olympia', 'The Colosseum', 'Göbekli Tepe', 'The Long Wall', 'Fuji Heights', 'Voyagers’ Atoll', 'The Cloud Terraces'];
const minus = (n: number) => (n < 0 ? `−${-n}` : String(n));

interface Places {
  gold: number[];
  silver: number[];
  bronze: number[];
  scenic: number[];
  totals: number[];
}

/** Who gets what. Gold is the winners (a playoff decides ties by default); the rest by total. Exported for tests. */
export function places(state: Pick<GameState, 'players' | 'winners'>): Places {
  const totals = state.players.map((p) => p.holeScores.reduce((a, b) => a + b, 0));
  const n = totals.length;
  const gold = (state.winners ?? []).slice();
  if (!gold.length) {
    const best = Math.min(...totals);
    totals.forEach((t, i) => t === best && gold.push(i));
  }
  const rest = totals.map((_, i) => i).filter((i) => !gold.includes(i));
  const placeOf = (i: number) => gold.length + 1 + rest.filter((j) => totals[j]! < totals[i]!).length;
  const silver = n >= 3 ? rest.filter((i) => placeOf(i) === 2) : [];
  const bronze = n >= 4 ? rest.filter((i) => placeOf(i) === 3) : [];
  const medaled = new Set([...gold, ...silver, ...bronze]);
  const others = rest.filter((i) => !medaled.has(i));
  const worst = others.length ? Math.max(...others.map((i) => totals[i]!)) : null;
  const scenic = n >= 3 && worst !== null ? others.filter((i) => totals[i] === worst) : [];
  return { gold, silver, bronze, scenic, totals };
}

interface Recap {
  tag: string;
  seat: number;
  chip: string;
  weight: number;
}

function recaps(state: GameState, mem: GameMemory, gold: number[]): Recap[] {
  const out: Recap[] = [];
  const n = state.players.length;
  const uniqueMax = (xs: number[], min: number): number | null => {
    const m = Math.max(0, ...xs);
    if (m < min) return null;
    const who = xs.map((x, i) => (x === m ? i : -1)).filter((i) => i >= 0);
    return who.length === 1 ? who[0]! : null;
  };
  if (mem.bestHole && mem.bestHole.score <= -10) {
    out.push({ tag: 'best', seat: mem.bestHole.seat, chip: `Best hole ${minus(mem.bestHole.score)} · ${WORLD_NAMES[mem.bestHole.world - 1]}`, weight: 3 });
  }
  const eu = uniqueMax(mem.eurekas, 2);
  if (eu !== null) out.push({ tag: 'eurekas', seat: eu, chip: `${mem.eurekas[eu]} Eureka pairs`, weight: 2 });
  const wire = wireToWire(state, mem);
  if (wire !== null) out.push({ tag: 'wire', seat: wire, chip: 'Led after every hole', weight: 2 });
  if (n >= 3 && gold.length === 1) {
    const g = gold[0]!;
    const lastAt = mem.lastHistory.findIndex((l, i) => i >= 1 && i < mem.lastHistory.length - 1 && l.includes(g));
    if (lastAt >= 0) out.push({ tag: 'comeback', seat: g, chip: `Last after hole ${lastAt + 1}`, weight: 3 });
  }
  if (mem.worstHole && mem.worstHole.score >= 30) out.push({ tag: 'bravest', seat: mem.worstHole.seat, chip: `Bravest hole · ${WORLD_NAMES[mem.worstHole.world - 1]}`, weight: 1 });
  const pat = uniqueMax(mem.skips, 4);
  if (pat !== null) out.push({ tag: 'patient', seat: pat, chip: `${mem.skips[pat]} cards kept hidden`, weight: 1 });
  const outs = uniqueMax(mem.outs, 3);
  if (outs !== null) out.push({ tag: 'outs', seat: outs, chip: `First out ${mem.outs[outs]} times`, weight: 1 });
  return out;
}

function wireToWire(state: GameState, mem: GameMemory): number | null {
  const h = mem.leadHistory;
  if (h.length < 3 || h.length < state.totalHoles) return null;
  const first = h[0];
  if (!first || first.length !== 1) return null;
  const w = first[0]!;
  return h.every((l) => l.length === 1 && l[0] === w) && (state.winners ?? []).includes(w) ? w : null;
}

/** The full ceremony for a finished game. Stored in the memory so replays are identical. */
export function composeCeremony(state: GameState, memory: GameMemory, ctx: SelectCtx): { ceremony: Ceremony; memory: GameMemory } {
  if (memory.ceremony) return { ceremony: memory.ceremony, memory };
  const mem = structuredCloneMemory(memory);
  const pl = places(state);
  const n = state.players.length;
  const full = state.totalHoles === 9 || state.totalHoles === 18;
  const beats: CeremonyBeat[] = [];
  const r = (slot: number) => rand(ctx.seed, 900, slot);
  const say = (kind: BeatKind, trigger: Trigger, env: Env, seats: number[], slot: number, chip?: string) => {
    let lines = eligible(trigger, { full, ...env }, mem, ctx);
    // A short tour says so when it can.
    if (!full && lines.some((l) => l.tags.includes('short'))) lines = lines.filter((l) => l.tags.includes('short'));
    const line = pickLine(lines, r(slot), ctx.history);
    if (!line) return;
    commit(mem, line);
    beats.push({ kind, lineId: line.id, seats, ...(chip ? { chip } : {}) });
  };

  const goldSeat = pl.gold[0]!;
  const others = pl.totals.map((_, i) => i).filter((i) => !pl.gold.includes(i)).sort((a, b) => pl.totals[a]! - pl.totals[b]! || a - b);
  const runnerUp = others[0];
  const wire = wireToWire(state, mem);
  const goldVariants = new Set<string>();
  if (state.hole > state.totalHoles && pl.gold.length === 1) goldVariants.add('playoff');
  else if (wire !== null) goldVariants.add('wire');
  else if (runnerUp !== undefined && pl.totals[runnerUp]! - pl.totals[goldSeat]! <= 2) goldVariants.add('close');

  say('open', 'ceremonyOpen', {}, [], 1);

  // Last place already gets a whole award: no second, rueful mention of the same player.
  const lastSeats = n === 2 ? others : pl.scenic;
  const highs = recaps(state, mem, pl.gold).filter(
    (x) => !(x.tag === 'wire' && goldVariants.has('wire')) && !((x.tag === 'bravest' || x.tag === 'outs') && lastSeats.includes(x.seat)),
  );
  if (highs.length) {
    const total = highs.reduce((s, h) => s + h.weight, 0);
    let x = r(2) * total;
    const h = highs.find((c) => (x -= c.weight) < 0) ?? highs[highs.length - 1]!;
    say('highlight', 'recap', { recap: h.tag }, [h.seat], 3, h.chip);
  }

  if (n === 2) {
    if (pl.gold.length >= 2) say('gold', 'goldShared', { twoNames: true }, pl.gold, 4);
    else say('twoPlayer', 'twoPlayer', { twoNames: true }, [goldSeat, others[0]!], 4);
  } else {
    if (pl.scenic.length) {
      const sc = pl.scenic[0]!;
      const above = pl.totals.filter((_, i) => !pl.scenic.includes(i)).reduce((m, t) => Math.max(m, t), -Infinity);
      const v = new Set<string>();
      if (pl.totals[sc]! - pl.totals[goldSeat]! >= 40) v.add('far');
      else if (pl.totals[sc]! - above <= 2) v.add('close');
      say('scenic', 'scenic', { variants: v }, pl.scenic, 5);
    }
    if (pl.bronze.length) say('bronze', 'bronze', {}, pl.bronze, 6);
    if (pl.silver.length) {
      const sv = pl.silver[0]!;
      const v = new Set<string>();
      const behind = pl.totals.filter((t, i) => !pl.gold.includes(i) && !pl.silver.includes(i) && t > pl.totals[sv]!);
      if (pl.totals[sv]! - pl.totals[goldSeat]! <= 2) v.add('close');
      else if (behind.length && Math.min(...behind) - pl.totals[sv]! >= 10) v.add('clear');
      say('silver', 'silver', { variants: v }, pl.silver, 7);
    }
    if (pl.gold.length >= 2) say('gold', 'goldShared', { twoNames: true }, pl.gold, 8);
    else say('gold', 'gold', { variants: goldVariants }, [goldSeat], 8);
  }
  say('close', 'ceremonyClose', {}, [], 9);

  const ceremony: Ceremony = { beats, gold: pl.gold, silver: pl.silver, bronze: pl.bronze, scenic: pl.scenic };
  mem.ceremony = ceremony;
  return { ceremony, memory: mem };
}

/** The line for an id (throws on an unknown id: the library and the selection must agree). */
export function lineOf(id: string): Line {
  const l = LINE_BY_ID.get(id);
  if (!l) throw new Error(`unknown Tibbs line ${id}`);
  return l;
}
